import type { ParagraphTextImages as ParagraphTextImagesData } from "@/lib/api/cms-catalog";
import { ParagraphHeading } from "../shared/ParagraphHeading";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import {
  PARAGRAPH_TEXT_IMAGES_LEFT_LAYOUT,
  PARAGRAPH_TEXT_IMAGES_RIGHT_LAYOUT,
  PARAGRAPH_TEXT_IMAGES_TOP_LAYOUT,
  PARAGRAPH_TEXT_IMAGES_BOT_LAYOUT,
  PARAGRAPH_TWO_COLS_TEXT_GALLERY_LAYOUT,
} from "@/lib/cms/paragraphLayouts";
import { HtmlText } from "../shared/HtmlText";
import { ImageGallery } from "../shared/ImageGallery";
import { Attachments } from "../shared/Attachments";
import styles from "../Paragraph.module.css";

/**
 * Layouts 2-5, 13 — `PARAGRAPH_TEXT_IMAGES_*_LAYOUT` + `TWO_COLS_TEXT_GALLERY`.
 *
 * **WAZNE - konwencja Imperial:** Nazwy stalych `LEFT`/`RIGHT` odnosza sie do pozycji
 * **TEKSTU**, NIE obrazow. Z perspektywy UI:
 *   - 2 (LEFT - tekst z lewej)  → obrazy z **PRAWEJ**, tekst z lewej (flex-row)
 *   - 3 (RIGHT - tekst z prawej) → obrazy z **LEWEJ**, tekst z prawej (flex-row reverse)
 *   - 13 (GALLERY) — to samo co 2 (tekst | galeria, gallery po prawej)
 *   - 4 (TOP)    — obrazy nad tekstem (kolumna; obrazy pierwsze)
 *   - 5 (BOTTOM) — tekst nad obrazami (kolumna; tekst pierwszy)
 *
 * Patrz: IPARAGRAPH_LAYOUT_SCHEMATIC.php komentarz nad mapping tl/tr i lang
 * dict `advenced_layouts[2]="po prawej"`, `[3]="po lewej"`.
 *
 * DOM order = wizualny order (mobile reflow naturalnie zachowuje). Dla layoutu
 * 3 (RIGHT) używamy `flexRowReverse` żeby DOM trzymał tekst pierwszy (a11y SR),
 * a wizualnie obrazy były z lewej.
 */
export function ParagraphTextImages({
  paragraph,
  t,
}: {
  paragraph: ParagraphTextImagesData;
  t: ParagraphTranslations;
}) {
  const text = <HtmlText html={paragraph.text} />;
  /* Wybór wariantu galerii per layout:
   * - 2/3 (LEFT/RIGHT) — pionowy stack, obrazy 100% szerokości mediaCol obok tekstu
   * - 4/5 (TOP/BOT) — wide-grid, kafle ~280px min (pełna szerokość kontenera = duże miniatury)
   * - 13 (GALLERY) — default grid, kafle ~160px min (przy textCol mniej miejsca) */
  const variant: "stack" | "wide-grid" | "grid" =
    paragraph.layout === PARAGRAPH_TEXT_IMAGES_LEFT_LAYOUT ||
    paragraph.layout === PARAGRAPH_TEXT_IMAGES_RIGHT_LAYOUT
      ? "stack"
      : paragraph.layout === PARAGRAPH_TEXT_IMAGES_TOP_LAYOUT ||
        paragraph.layout === PARAGRAPH_TEXT_IMAGES_BOT_LAYOUT
      ? "wide-grid"
      : "grid";
  const media = (
    <ImageGallery images={paragraph.images} t={t} variant={variant} />
  );

  return (
    <section className={styles.paragraph}>
      <ParagraphHeading name={paragraph.name} centerHeader={paragraph.center_header} />
      {renderLayout(paragraph.layout, text, media)}
      <Attachments files={paragraph.attachments} t={t} />
    </section>
  );
}

function renderLayout(
  layout: ParagraphTextImagesData["layout"],
  text: React.ReactNode,
  media: React.ReactNode,
) {
  switch (layout) {
    case PARAGRAPH_TEXT_IMAGES_LEFT_LAYOUT:
    case PARAGRAPH_TWO_COLS_TEXT_GALLERY_LAYOUT:
      /* Layout 2 (LEFT - tekst z lewej, obrazy z prawej) i 13 (gallery po prawej).
         DOM: tekst pierwszy, media drugie - wizualnie tak samo (flexRow normalny). */
      return (
        <div className={styles.flexRow}>
          <div className={styles.textCol}>{text}</div>
          <div className={styles.mediaCol}>{media}</div>
        </div>
      );
    case PARAGRAPH_TEXT_IMAGES_RIGHT_LAYOUT:
      /* Layout 3 (RIGHT - tekst z prawej, obrazy z lewej). Renderujemy text
         FIRST w DOM (a11y SR), `flexRowReverse` układa wizualnie media z lewej.
         Mobile reflow: tekst pierwszy. */
      return (
        <div className={`${styles.flexRow} ${styles.flexRowReverse}`}>
          <div className={styles.textCol}>{text}</div>
          <div className={styles.mediaCol}>{media}</div>
        </div>
      );
    case PARAGRAPH_TEXT_IMAGES_TOP_LAYOUT:
      /* Obrazy nad — media pierwsze w DOM. */
      return (
        <div className={styles.flexCol}>
          <div>{media}</div>
          <div>{text}</div>
        </div>
      );
    case PARAGRAPH_TEXT_IMAGES_BOT_LAYOUT:
      return (
        <div className={styles.flexCol}>
          <div>{text}</div>
          <div>{media}</div>
        </div>
      );
    default:
      return (
        <div className={styles.flexCol}>
          {text}
          {media}
        </div>
      );
  }
}
